/**
 * NY Cart Upsell — "People Also Bought"
 * Custom element for Nine Yards Coffee cart drawer recommendations.
 *
 * - Fetches Shopify product recommendations
 * - Filters out products already in cart
 * - Shows only Size variants (250g, 500g, 1kg), defaults Grind to "Whole Beans"
 * - CSS scroll-snap carousel (no external libs)
 * - AJAX add to cart with Dawn section re-render
 */

if (!customElements.get('ny-cart-upsell')) {
  class NyCartUpsell extends HTMLElement {
    constructor() {
      super();
      this.productId = null;
      this.cartProductIds = [];
    }

    connectedCallback() {
      this.init();
    }

    async init() {
      try {
        // Get product IDs from cart
        const cartData = await this.getCartData();
        if (!cartData || cartData.items.length === 0) {
          this.hide();
          return;
        }

        this.cartProductIds = cartData.items.map(item => item.product_id);
        this.productId = this.cartProductIds[0];

        // Fetch recommendations
        const products = await this.fetchRecommendations(this.productId);
        if (!products || products.length === 0) {
          this.hide();
          return;
        }

        // Filter out products already in cart
        const filtered = products.filter(
          p => !this.cartProductIds.includes(p.id)
        );

        if (filtered.length === 0) {
          this.hide();
          return;
        }

        this.render(filtered);
        this.removeAttribute('hidden');
      } catch (err) {
        console.error('[ny-cart-upsell] init error:', err);
        this.hide();
      }
    }

    async getCartData() {
      // Try data attribute first
      const attrId = this.getAttribute('data-product-id');
      if (attrId) {
        // Still need full cart for filtering, so fetch anyway
      }
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return null;
      return res.json();
    }

    async fetchRecommendations(productId) {
      const url = `/recommendations/products.json?product_id=${productId}&limit=8&intent=related`;
      const res = await fetch(url);
      if (!res.ok) return [];
      const data = await res.json();
      return data.products || [];
    }

    /**
     * Filter variants to only show consumer sizes (250g, 500g, 1kg).
     * Skip bulk (2kg, 5kg, 10kg) and default Grind to "Whole Beans".
     */
    getDisplayVariants(product) {
      // Find which option position is "Size"
      const sizeOptionIndex = product.options.findIndex(
        opt => (typeof opt === 'string' ? opt : opt.name || '').toLowerCase() === 'size'
      );
      const grindOptionIndex = product.options.findIndex(
        opt => (typeof opt === 'string' ? opt : opt.name || '').toLowerCase() === 'grind'
      );

      if (sizeOptionIndex === -1) {
        // No size option — single variant product or no size differentiation
        return {
          sizes: [],
          variants: product.variants,
          defaultVariantId: product.variants[0]?.id
        };
      }

      // Allowed size values (consumer sizes only)
      const allowedSizes = ['250g', '500g', '1kg'];

      // Get unique size values from variants, filtered to allowed sizes
      const sizeValues = [];
      const seen = new Set();

      product.variants.forEach(v => {
        const sizeVal = v[`option${sizeOptionIndex + 1}`];
        if (!sizeVal) return;

        const normalised = sizeVal.toLowerCase().replace(/\s/g, '');
        if (allowedSizes.includes(normalised) && !seen.has(normalised)) {
          seen.add(normalised);
          sizeValues.push(sizeVal); // Keep original casing
        }
      });

      // Find default variant: first size + "Whole Beans" grind (if grind exists)
      let defaultVariantId = null;
      if (sizeValues.length > 0) {
        const firstSize = sizeValues[0];
        const match = product.variants.find(v => {
          const matchesSize = v[`option${sizeOptionIndex + 1}`] === firstSize;
          if (grindOptionIndex === -1) return matchesSize;
          const grindVal = v[`option${grindOptionIndex + 1}`];
          return matchesSize && grindVal && grindVal.toLowerCase().includes('whole');
        });
        defaultVariantId = match ? match.id : product.variants[0]?.id;
      } else {
        defaultVariantId = product.variants[0]?.id;
      }

      return {
        sizes: sizeValues,
        variants: product.variants,
        sizeOptionIndex,
        grindOptionIndex,
        defaultVariantId
      };
    }

    /**
     * Given a size selection, find the best variant ID
     * (matching that size + "Whole Beans" grind)
     */
    findVariantForSize(product, sizeValue, sizeOptionIndex, grindOptionIndex) {
      const match = product.variants.find(v => {
        const matchesSize = v[`option${sizeOptionIndex + 1}`] === sizeValue;
        if (grindOptionIndex === -1) return matchesSize;
        const grindVal = v[`option${grindOptionIndex + 1}`];
        return matchesSize && grindVal && grindVal.toLowerCase().includes('whole');
      });
      // Fallback: just match size, any grind
      if (!match) {
        return product.variants.find(
          v => v[`option${sizeOptionIndex + 1}`] === sizeValue
        );
      }
      return match;
    }

    render(products) {
      const heading = document.createElement('p');
      heading.className = 'ny-cart-upsell__heading';
      heading.textContent = 'People Also Bought';

      const track = document.createElement('div');
      track.className = 'ny-cart-upsell__track';

      products.slice(0, 6).forEach(product => {
        const card = this.createCard(product);
        track.appendChild(card);
      });

      this.innerHTML = '';
      this.appendChild(heading);
      this.appendChild(track);
    }

    createCard(product) {
      const { sizes, sizeOptionIndex, grindOptionIndex, defaultVariantId } =
        this.getDisplayVariants(product);

      const card = document.createElement('div');
      card.className = 'ny-cart-upsell__card';
      card.dataset.selectedVariantId = defaultVariantId || '';

      // Image
      const imageWrap = document.createElement('div');
      imageWrap.className = 'ny-cart-upsell__image-wrap';

      const img = document.createElement('img');
      img.className = 'ny-cart-upsell__image';
      img.src = this.resizeImage(product.featured_image, '300x');
      img.alt = product.title;
      img.loading = 'lazy';
      img.width = 150;
      img.height = 150;
      imageWrap.appendChild(img);

      // Body
      const body = document.createElement('div');
      body.className = 'ny-cart-upsell__body';

      // Title
      const title = document.createElement('p');
      title.className = 'ny-cart-upsell__title';
      title.textContent = product.title;

      // Price
      const price = document.createElement('p');
      price.className = 'ny-cart-upsell__price';
      const priceVal = defaultVariantId
        ? (product.variants.find(v => v.id === defaultVariantId)?.price || product.price) / 100
        : product.price / 100;
      price.textContent = this.formatPrice(priceVal);

      body.appendChild(title);
      body.appendChild(price);

      // Variant pills (Size only)
      if (sizes.length > 1) {
        const pillsWrap = document.createElement('div');
        pillsWrap.className = 'ny-cart-upsell__variants';

        sizes.forEach((sizeVal, i) => {
          const pill = document.createElement('button');
          pill.type = 'button';
          pill.className = 'ny-cart-upsell__pill';
          pill.textContent = sizeVal;
          pill.setAttribute('aria-pressed', i === 0 ? 'true' : 'false');

          pill.addEventListener('click', () => {
            // Update selected state
            pillsWrap.querySelectorAll('.ny-cart-upsell__pill').forEach(p => {
              p.setAttribute('aria-pressed', 'false');
            });
            pill.setAttribute('aria-pressed', 'true');

            // Find matching variant
            const variant = this.findVariantForSize(
              product, sizeVal, sizeOptionIndex, grindOptionIndex
            );
            if (variant) {
              card.dataset.selectedVariantId = variant.id;
              // Update price
              price.textContent = this.formatPrice(variant.price / 100);
            }
          });

          pillsWrap.appendChild(pill);
        });

        body.appendChild(pillsWrap);
      }

      // Add to Cart button
      const addBtn = document.createElement('button');
      addBtn.type = 'button';
      addBtn.className = 'ny-cart-upsell__add-btn';
      addBtn.textContent = 'Add';

      addBtn.addEventListener('click', () => {
        this.handleAddToCart(card, addBtn);
      });

      body.appendChild(addBtn);

      card.appendChild(imageWrap);
      card.appendChild(body);

      return card;
    }

    async handleAddToCart(card, btn) {
      const variantId = card.dataset.selectedVariantId;
      if (!variantId || btn.disabled) return;

      btn.disabled = true;
      btn.textContent = 'Adding...';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(variantId), quantity: 1 }] }),
          credentials: 'same-origin'
        });

        if (!res.ok) throw new Error('Add to cart failed');

        // Show confirmation
        btn.textContent = 'Added ✓';
        btn.classList.add('ny-cart-upsell__add-btn--added');

        // Re-render cart drawer via Dawn's section rendering
        await this.refreshCartDrawer();

        // Re-init recommendations (added product should disappear)
        setTimeout(() => this.init(), 300);

      } catch (err) {
        console.error('[ny-cart-upsell] add to cart error:', err);
        btn.textContent = 'Add';
        btn.disabled = false;
      }
    }

    async refreshCartDrawer() {
      try {
        const res = await fetch('/?sections=cart-drawer');
        if (!res.ok) return;
        const sections = await res.json();
        const html = sections['cart-drawer'];
        if (!html) return;

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Update cart items
        const newItems = doc.querySelector('cart-drawer-items');
        const oldItems = document.querySelector('cart-drawer-items');
        if (newItems && oldItems) {
          oldItems.innerHTML = newItems.innerHTML;
        }

        // Update cart count badge
        const newBubble = doc.querySelector('.cart-count-bubble');
        const oldBubble = document.querySelector('.cart-count-bubble');
        if (newBubble && oldBubble) {
          oldBubble.innerHTML = newBubble.innerHTML;
        }

        // Update footer (subtotal)
        const newFooter = doc.querySelector('.drawer__footer');
        const oldFooter = document.querySelector('.drawer__footer');
        if (newFooter && oldFooter) {
          oldFooter.innerHTML = newFooter.innerHTML;
        }

        // Update header cart count
        const newHeader = doc.querySelector('#cart-icon-bubble');
        const oldHeader = document.querySelector('#cart-icon-bubble');
        if (newHeader && oldHeader) {
          oldHeader.innerHTML = newHeader.innerHTML;
        }

      } catch (err) {
        console.error('[ny-cart-upsell] refresh error:', err);
      }
    }

    resizeImage(url, size) {
      if (!url) return '';
      // Shopify CDN image resize
      if (url.includes('cdn.shopify.com')) {
        return url.replace(/(\.[a-z]+)(\?|$)/, `_${size}$1$2`);
      }
      return url;
    }

    formatPrice(amount) {
      return `$${amount.toFixed(2)} AUD`;
    }

    hide() {
      this.setAttribute('hidden', '');
      this.innerHTML = '';
    }
  }

  customElements.define('ny-cart-upsell', NyCartUpsell);
}
